import { format } from "date-fns";
import {
  Phone, AlertTriangle, Activity, ShieldAlert,
  Pill, FileText, UserRound, Edit, Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface Patient {
  id: string;
  name: string;
  age?: number;
  gender?: string;
  phone?: string;
  phone_2?: string;
  email?: string;
  address?: string;
  city?: string;
  pin_code?: string;
  preferred_language?: string;
  aadhar_number?: string;
  blood_group?: string;
  allergies?: string;
  chronic_conditions?: string;
  current_medications?: string;
  medical_history?: string;
  mrn?: string;
  status?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  date_of_birth?: string;
  payment_preference?: string;
  created_at: string;
}

interface Props {
  patient: Patient;
  onEdit: () => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border last:border-0">
      <span className="text-xs text-muted-foreground shrink-0 pt-0.5">{label}</span>
      <span className="text-sm font-medium text-foreground text-right break-all">{value}</span>
    </div>
  );
}

function SectionLabel({ label }: { label: string }) {
  return (
    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">{label}</p>
  );
}

function formatDob(dob: string): string {
  try {
    const [y, m, d] = dob.split("-").map(Number);
    return format(new Date(y, m - 1, d), "d MMM yyyy");
  } catch {
    return dob;
  }
}

const STATUS_STYLE: Record<string, string> = {
  Active:   "bg-success/10 text-success border-success/20",
  Inactive: "bg-muted text-muted-foreground border-border",
  Archived: "bg-destructive/10 text-destructive border-destructive/20",
};

export default function PatientProfileTab({ patient, onEdit }: Props) {
  const statusLabel = patient.status || "Active";
  const statusStyle = STATUS_STYLE[statusLabel] ?? STATUS_STYLE.Active;

  const hasContact = !!(patient.phone || patient.phone_2 || patient.email);
  const hasAddress = !!(patient.address || patient.city || patient.pin_code);
  const hasEmergency = !!(patient.emergency_contact_name);
  const hasMedicalBackground = !!(
    patient.allergies || patient.chronic_conditions ||
    patient.current_medications || patient.medical_history
  );
  const hasPersonalFacts = !!(
    patient.blood_group || patient.date_of_birth ||
    patient.preferred_language || patient.status
  );

  return (
    <div className="space-y-4 p-3">

      {/* ── Personal facts + Edit ─────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 bg-card border border-border rounded-lg px-4 py-2.5 flex-wrap gap-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${statusStyle}`}>
            {statusLabel}
          </span>
          {patient.blood_group && (
            <span className="text-xs font-bold bg-destructive/10 text-destructive border border-destructive/20 px-2 py-0.5 rounded-full flex items-center gap-1">
              <Heart className="w-3 h-3" /> {patient.blood_group}
            </span>
          )}
          {patient.date_of_birth && (
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              DOB: {formatDob(patient.date_of_birth)}
            </span>
          )}
          {patient.preferred_language && (
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {patient.preferred_language}
            </span>
          )}
          {!hasPersonalFacts && (
            <span className="text-xs text-muted-foreground italic">No additional details</span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={onEdit} className="gap-1.5 shrink-0">
          <Edit className="w-3.5 h-3.5" /> Edit Profile
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* ── Left column: Contact + Emergency ─────────────────────────── */}
        <div className="space-y-4">

          {/* Contact */}
          {(hasContact || hasAddress) ? (
            <div className="bg-card border border-border rounded-lg p-3">
              <SectionLabel label="Contact" />
              <div>
                {patient.phone && (
                  <Row label="Phone" value={
                    <a href={`tel:${patient.phone}`} className="text-primary hover:underline">{patient.phone}</a>
                  } />
                )}
                {patient.phone_2 && (
                  <Row label="Alt Phone" value={
                    <a href={`tel:${patient.phone_2}`} className="text-primary hover:underline">{patient.phone_2}</a>
                  } />
                )}
                {patient.email && <Row label="Email" value={patient.email} />}
                {hasAddress && (
                  <div className="py-2 border-b border-border last:border-0">
                    <span className="text-xs text-muted-foreground">Address</span>
                    <div className="text-sm font-medium text-foreground mt-0.5">
                      {patient.address && <div>{patient.address}</div>}
                      {(patient.city || patient.pin_code) && (
                        <div className="text-muted-foreground text-xs mt-0.5">
                          {patient.city}{patient.city && patient.pin_code && " – "}{patient.pin_code}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-5 text-center">
              <Phone className="w-5 h-5 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-2">No contact details recorded</p>
              <Button variant="ghost" size="sm" onClick={onEdit} className="text-xs">Add contact</Button>
            </div>
          )}

          {/* Emergency Contact */}
          {hasEmergency && (
            <div className="bg-card border border-border rounded-lg p-3">
              <SectionLabel label="Emergency Contact" />
              <div>
                <Row label="Name" value={patient.emergency_contact_name} />
                {patient.emergency_contact_phone && (
                  <Row label="Phone" value={
                    <a href={`tel:${patient.emergency_contact_phone}`} className="text-primary hover:underline">
                      {patient.emergency_contact_phone}
                    </a>
                  } />
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Right column: Medical Background ─────────────────────────── */}
        <div>
          {hasMedicalBackground ? (
            <div className="bg-card border border-border rounded-lg p-3 h-full">
              <SectionLabel label="Medical Background" />
              <div className="space-y-4">

                {/* Allergies — safety critical */}
                {patient.allergies && (
                  <div className="p-2.5 bg-destructive/5 border border-destructive/20 rounded-lg">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-destructive" />
                      <span className="text-xs font-semibold text-destructive">Allergies</span>
                    </div>
                    <p className="text-sm text-foreground">{patient.allergies}</p>
                  </div>
                )}

                {/* Chronic Conditions */}
                {patient.chronic_conditions && (
                  <div className="p-2.5 bg-warning/5 border border-warning/20 rounded-lg">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Activity className="w-3.5 h-3.5 text-warning" />
                      <span className="text-xs font-semibold text-warning">Chronic Conditions</span>
                    </div>
                    <p className="text-sm text-foreground">{patient.chronic_conditions}</p>
                  </div>
                )}

                {/* Current Medications */}
                {patient.current_medications && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Pill className="w-3.5 h-3.5 text-primary" />
                      <span className="text-xs font-semibold text-primary">Current Medications</span>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed">{patient.current_medications}</p>
                  </div>
                )}

                {/* Medical History */}
                {patient.medical_history && (
                  <div className={patient.current_medications ? "pt-3 border-t border-border" : ""}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-semibold text-muted-foreground">Medical History</span>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed">{patient.medical_history}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-5 text-center h-full flex flex-col items-center justify-center gap-2">
              <ShieldAlert className="w-5 h-5 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No medical background recorded</p>
              <p className="text-xs text-muted-foreground">Add allergies, conditions, or medications via Edit</p>
              <Button variant="ghost" size="sm" onClick={onEdit} className="text-xs mt-1">Add now</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
